import React, { useCallback, useMemo } from 'react';
import { useBuildContactOptions2 } from '../../../buildContactOptions';
import { eCreateSigningAppointmentFormDataOwnerTypes } from '../../../../../../types/order/consumer/eCreateSigningAppointmentFormDataOwnerTypes';
import { ComboBoxTW } from '../../../../../../tailwindComponents/selectTW/combobox/ComboBoxTW';
import { useField } from 'formik';
import { SelectOption, SelectTWProps } from '../../../../../../tailwindComponents/selectTW/interfaces';
import { ISignerFormData } from '../../../../../../types/signingAppointment/ISignerFormData';
import { UUID } from '../../../../../../types/shared/UUID';
import { isEqual, isNil } from 'lodash';

export const SignerAdditionSection = ({
  index,
  ownerType,
  replace,
}: {
  index: number;
  ownerType: eCreateSigningAppointmentFormDataOwnerTypes;
  replace: (index: number, value: ISignerFormData | undefined) => void;
}) => {
  const { options, dictionary } = useBuildContactOptions2(ownerType);

  const onChange = useCallback(
    (option: SelectOption) => {
      const signer = dictionary.get(String(option.value));
      replace(index, signer);
    },
    [dictionary, index, replace]
  );

  return (
    <SpecialSelectFieldTWSingle
      name={'signers'}
      options={options}
      onChange={onChange}
      placeholder={'Select signer'}
      index={index}
      dictionary={dictionary}
      labelText={'Existing Order Contact'}
    />
  );
};

interface SpecialSelectFieldTWProps extends SelectTWProps {
  name: string;
  onChange?: (option: SelectOption) => void;
  onClearSelectionProp?: () => void;
  index: number;
  dictionary: Map<UUID, ISignerFormData>;
}

const SpecialSelectFieldTWSingle: React.FC<SpecialSelectFieldTWProps> = ({
  name,
  options,
  onChange: onChangeProp,
  onClearSelectionProp,
  index,
  dictionary,
  ...rest
}) => {
  const [fieldInputProps, fieldMetaProps, fieldHelperProps] = useField<ISignerFormData[] | null>({ name });

  const onChange = useCallback(
    (option: SelectOption) => {
      fieldHelperProps.setTouched(true);
      if (onChangeProp) {
        onChangeProp(option);
      }
    },
    [fieldHelperProps, onChangeProp]
  );
  const onBlur = useCallback(() => {
    fieldHelperProps.setTouched(true);
  }, [fieldHelperProps]);

  const onClearSelection = useCallback(() => {
    fieldHelperProps.setTouched(true);
    fieldHelperProps.setValue(null);
    if (onClearSelectionProp) {
      onClearSelectionProp();
    }
  }, [fieldHelperProps, onClearSelectionProp]);

  const boxValue: SelectOption | null = useMemo(() => {
    if (!fieldInputProps.value) {
      return null;
    }

    const signerFormData = fieldInputProps.value.at(index);
    if (!signerFormData) {
      return null;
    }
    return (
      options.find((option) => {
        return isEqual(dictionary.get(`${option.value}`), signerFormData);
      }) ?? null
    );
  }, [fieldInputProps, index, options, dictionary]);

  const elementLevelErrorText = useMemo(() => {
    if (!fieldMetaProps.touched) {
      return undefined;
    }
    if (!Array.isArray(fieldMetaProps.error)) {
      return undefined;
    }

    if (isNil(fieldMetaProps.error[index])) {
      return undefined;
    }
    return JSON.stringify(Object.values(fieldMetaProps.error[index]).join(' ')).slice(1, -1);
  }, [fieldMetaProps, index]);

  return (
    <ComboBoxTW
      options={options}
      {...rest}
      {...fieldInputProps}
      onChange={onChange}
      onClearSelection={onClearSelection}
      onBlur={onBlur}
      value={boxValue}
      errorText={elementLevelErrorText}
    />
  );
};
